import { API_BASE } from '../http';
import type { RequestFn } from '../http';
import { withCsrfHeader } from '../../csrf';

/** Projektverwaltung (portal-eigen) — Backend: routes/projects.ts, routes/projectTemplates.ts. */

export type ProjectPhase = 'BEFORE' | 'DURING' | 'AFTER';
export type ProjectTaskKind = 'TASK' | 'PHOTOS' | 'MILESTONE';
export type ProjectStatus = 'PLANNED' | 'IN_PROGRESS' | 'ON_HOLD' | 'DONE' | 'CANCELLED';
export type ProjectListView = 'open' | 'closed' | 'all' | 'archived';

export interface ProjectPerson {
  id: string;
  displayName: string;
}

export interface ProjectCustomerRef {
  id: string;
  name: string;
  customerNumber: string | null;
}

/** Aufgaben und Foto-Aufgaben; Meilensteine zählen getrennt (`milestones`). */
export interface ProjectProgress {
  total: number;
  done: number;
  byPhase: Record<ProjectPhase, { total: number; done: number }>;
  openPhotoTasks: number;
  milestones: { total: number; done: number };
}

export type TimelineStatus = 'reached' | 'overdue' | 'today' | 'upcoming' | 'undated';

/** Punkt auf dem Zeitstrahl (services/projects/projectPlan.ts → buildTimeline). */
export interface TimelineEntry {
  /** Meilenstein-ID; Plan-Start und Plan-Ende haben keine. */
  id: string | null;
  type: 'start' | 'milestone' | 'end';
  title: string;
  description: string | null;
  date: string | null;
  reachedAt: string | null;
  status: TimelineStatus;
}

export interface ProjectListItem {
  id: string;
  number: string;
  title: string;
  status: ProjectStatus;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  startLabel: string | null;
  goLiveLabel: string | null;
  goLiveDate: string | null;
  customer: ProjectCustomerRef | null;
  lead: ProjectPerson | null;
  progress: ProjectProgress;
  mediaCount: number;
  visibleInPortal: boolean;
  nextMilestone: { title: string; date: string | null; status: TimelineStatus } | null;
  nextDueDate: string | null;
  overdueTasks: number;
  completedAt: string | null;
  archivedAt: string | null;
  updatedAt: string;
}

/** Bezug eines relativen Termins: Tag 0 (Plan-Start) oder zweiter Stichtag (Tomedo: Praxisstart). */
export type OffsetAnchor = 'START' | 'GO_LIVE';

/**
 * Baustein einer Vorlage (Tomedo: Cloud-Telefonie, KI-Telefonie, TI durch FixByte oder tomedo …).
 * Gleiche `group` = entweder/oder. Aufgaben mit `optionKey` entstehen nur mit ihrem Baustein.
 */
export interface TemplateOption {
  key: string;
  label: string;
  description?: string | null;
  group?: string | null;
  defaultSelected: boolean;
  /** Zeile für den Umfang (ersetzt `{{bausteine}}`). */
  scopeLine?: string | null;
  /** Zusätzlicher Aufwand in Stunden. */
  hours?: number | null;
}

export interface ProjectTask {
  id: string;
  phase: ProjectPhase;
  kind: ProjectTaskKind;
  title: string;
  description: string | null;
  sortOrder: number;
  minPhotos: number;
  assignee: ProjectPerson | null;
  /** Fest eingetragener Termin (JJJJ-MM-TT). */
  dueDate: string | null;
  /** Relativ zum Plan-Start bzw. zum zweiten Stichtag (aus der Vorlage). */
  startOffsetDays: number | null;
  offsetAnchor: OffsetAnchor;
  /** Baustein, aus dem die Aufgabe kam. */
  optionKey: string | null;
  /** Termin, der tatsächlich gilt — fest oder aus Stichtag + Versatz. */
  effectiveDueDate: string | null;
  doneAt: string | null;
  doneBy: ProjectPerson | null;
  photoCount: number;
  sourceTemplate: { id: string; name: string } | null;
}

export type ProjectMediaType = 'PHOTO' | 'VIDEO';

/** Foto oder Video im Original; `hasPreview` = es gibt ein kleines Vorschau-JPEG. */
export interface ProjectMedia {
  id: string;
  phase: ProjectPhase;
  taskId: string | null;
  mediaType: ProjectMediaType;
  caption: string | null;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  hasPreview: boolean;
  width: number | null;
  height: number | null;
  durationSeconds: number | null;
  /** Für Referenzen/Werbung vorgemerkt. */
  isReference: boolean;
  createdAt: string;
  uploadedBy: ProjectPerson;
}

export interface ProjectMediaUploadState {
  id: string;
  sizeBytes: number;
  receivedBytes: number;
  chunkBytes: number;
}

export interface CreateMediaUploadInput {
  filename: string;
  mimeType: string | null;
  sizeBytes: number;
  phase: ProjectPhase;
  taskId: string | null;
  width?: number | null;
  height?: number | null;
  durationSeconds?: number | null;
}

/** Ergebnis eines Stücks: angenommen, oder der Server hat einen anderen Stand (409). */
export type ChunkResult =
  | { ok: true; receivedBytes: number }
  | { ok: false; status: number; receivedBytes?: number; error: string; code?: string };

export interface ProjectDetail {
  id: string;
  number: string;
  title: string;
  status: ProjectStatus;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  /** Name von Tag 0 statt „Plan-Start" (aus der Vorlage, z. B. „Umstellungsstart"). */
  startLabel: string | null;
  /** Zweiter Stichtag (z. B. „Praxisstart"), Abstand zu Tag 0 (Umsetzungsdauer) und Datum. */
  goLiveLabel: string | null;
  goLiveOffsetDays: number | null;
  goLiveDate: string | null;
  /** Gewählte Bausteine der Vorlage. */
  options: TemplateOption[];
  scope: string | null;
  estimatedHours: number | null;
  customer: ProjectCustomerRef | null;
  lead: ProjectPerson | null;
  createdBy: ProjectPerson;
  completedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  progress: ProjectProgress;
  appliedTemplates: Array<{ id: string; name: string }>;
  timeline: TimelineEntry[];
  /** „Im Kundenportal zeigen" am Projekt. */
  visibleInPortal: boolean;
  /** Ob der Kunde es wirklich sieht: Portal und Bereich „Projekte" für ihn freigeschaltet. */
  customerPortal: { accessEnabled: boolean; projectsModuleEnabled: boolean } | null;
  tasks: ProjectTask[];
  media: ProjectMedia[];
}

export interface CreateProjectInput {
  title: string;
  customerId?: string | null;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  scope?: string | null;
  estimatedHours?: number | null;
  leadUserId?: string | null;
  templateId?: string | null;
  /** Gewählte Bausteine; ohne Angabe die Vorgaben der Vorlage. */
  optionKeys?: string[];
  /** Umsetzungsdauer in Tagen (Tag 0 → zweiter Stichtag); ohne Angabe der Standard der Vorlage. */
  goLiveOffsetDays?: number | null;
}

export type UpdateProjectInput = Partial<{
  title: string;
  customerId: string | null;
  status: ProjectStatus;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  scope: string | null;
  estimatedHours: number | null;
  startLabel: string | null;
  goLiveLabel: string | null;
  goLiveOffsetDays: number | null;
  leadUserId: string | null;
  visibleInPortal: boolean;
}>;

export interface CreateProjectTaskInput {
  /** Ohne Phase (Meilenstein): der Server leitet sie aus dem Termin ab. */
  phase?: ProjectPhase;
  kind?: ProjectTaskKind;
  title: string;
  description?: string | null;
  assigneeUserId?: string | null;
  dueDate?: string | null;
  minPhotos?: number;
}

export type UpdateProjectTaskInput = Partial<{
  title: string;
  description: string | null;
  phase: ProjectPhase;
  assigneeUserId: string | null;
  dueDate: string | null;
  minPhotos: number;
  done: boolean;
}>;

export interface ProjectTemplateTask {
  id?: string;
  phase: ProjectPhase;
  kind: ProjectTaskKind;
  title: string;
  description: string | null;
  sortOrder?: number;
  minPhotos: number;
  startOffsetDays: number | null;
  offsetAnchor: OffsetAnchor;
  optionKey: string | null;
}

export interface ProjectTemplate {
  id: string;
  name: string;
  description: string | null;
  defaultScope: string | null;
  defaultDurationDays: number | null;
  estimatedHours: number | null;
  /** Wie Tag 0 (Plan-Start) in Projekten dieser Vorlage heißt, z. B. „Umstellungsstart". */
  startLabel: string | null;
  /** Zweiter Stichtag und sein Standard-Abstand zu Tag 0 (Tomedo: „Praxisstart", 7). */
  goLiveLabel: string | null;
  goLiveOffsetDays: number | null;
  options: TemplateOption[];
  /** null bei mitgelieferten Vorlagen. */
  createdBy: ProjectPerson | null;
  /** Mitgelieferte FixByte-Standardvorlage (z. B. Tomedo-Wechsel) — bearbeitbar wie jede andere. */
  isBuiltin: boolean;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  usageCount: number;
  tasks: ProjectTemplateTask[];
}

export interface ProjectTemplateInput {
  name: string;
  description: string | null;
  defaultScope: string | null;
  defaultDurationDays: number | null;
  estimatedHours: number | null;
  startLabel: string | null;
  goLiveLabel: string | null;
  goLiveOffsetDays: number | null;
  options: TemplateOption[];
  tasks: Array<Omit<ProjectTemplateTask, 'id' | 'sortOrder'>>;
}

export function createProjectsApi(request: RequestFn) {
  return {
    async listProjects(params: { view?: ProjectListView; q?: string; customerId?: string; mine?: boolean } = {}) {
      const qs = new URLSearchParams();
      if (params.view) qs.set('view', params.view);
      if (params.q) qs.set('q', params.q);
      if (params.customerId) qs.set('customerId', params.customerId);
      if (params.mine) qs.set('mine', 'true');
      const query = qs.toString();
      return request<{ projects: ProjectListItem[] }>(`/projects${query ? `?${query}` : ''}`);
    },

    async getProjectPeople() {
      return request<{ users: ProjectPerson[] }>('/projects/people');
    },

    async getProject(id: string) {
      return request<{ project: ProjectDetail }>(`/projects/${encodeURIComponent(id)}`);
    },

    async createProject(data: CreateProjectInput) {
      return request<{ project: ProjectDetail }>('/projects', { method: 'POST', body: JSON.stringify(data) });
    },

    async updateProject(id: string, data: UpdateProjectInput) {
      return request<{ project: ProjectDetail }>(`/projects/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    async setProjectArchived(id: string, archived: boolean) {
      return request<{ project: ProjectDetail }>(
        `/projects/${encodeURIComponent(id)}/${archived ? 'archive' : 'unarchive'}`,
        { method: 'POST' },
      );
    },

    async applyProjectTemplate(id: string, templateId: string, optionKeys?: string[]) {
      return request<{ project: ProjectDetail; added: number; skipped: number }>(
        `/projects/${encodeURIComponent(id)}/apply-template`,
        { method: 'POST', body: JSON.stringify({ templateId, optionKeys }) },
      );
    },

    async createProjectTask(id: string, data: CreateProjectTaskInput) {
      return request<{ project: ProjectDetail }>(`/projects/${encodeURIComponent(id)}/tasks`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async updateProjectTask(id: string, taskId: string, data: UpdateProjectTaskInput) {
      return request<{ project: ProjectDetail }>(
        `/projects/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}`,
        { method: 'PATCH', body: JSON.stringify(data) },
      );
    },

    async moveProjectTask(id: string, taskId: string, direction: 'up' | 'down') {
      return request<{ project: ProjectDetail }>(
        `/projects/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}/move`,
        { method: 'POST', body: JSON.stringify({ direction }) },
      );
    },

    async deleteProjectTask(id: string, taskId: string) {
      return request<{ project: ProjectDetail }>(
        `/projects/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}`,
        { method: 'DELETE' },
      );
    },

    // ── Fotos & Videos: Upload in Stücken (routes/projectMedia.ts) ─────────────
    async createProjectMediaUpload(id: string, data: CreateMediaUploadInput) {
      return request<{ upload: ProjectMediaUploadState }>(`/projects/${encodeURIComponent(id)}/media/uploads`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async getProjectMediaUpload(id: string, uploadId: string) {
      return request<{ upload: ProjectMediaUploadState }>(
        `/projects/${encodeURIComponent(id)}/media/uploads/${encodeURIComponent(uploadId)}`,
      );
    },

    /**
     * Ein Stück als rohe Bytes. Direkt über fetch statt apiRequest: der setzt JSON als
     * Content-Type. Deshalb CSRF-Header selbst (CLAUDE.md, „CSRF — beide Portale").
     */
    async putProjectMediaChunk(
      id: string,
      uploadId: string,
      offset: number,
      chunk: Blob,
      signal?: AbortSignal,
    ): Promise<ChunkResult> {
      const res = await fetch(
        `${API_BASE}/projects/${encodeURIComponent(id)}/media/uploads/${encodeURIComponent(uploadId)}?offset=${offset}`,
        {
          method: 'PUT',
          headers: withCsrfHeader('PUT', { 'Content-Type': 'application/octet-stream' }),
          body: chunk,
          credentials: 'include',
          signal,
        },
      );
      let body: { upload?: ProjectMediaUploadState; receivedBytes?: number; error?: string; code?: string } = {};
      try {
        body = await res.json();
      } catch {
        /* kein JSON (z. B. Fehlerseite eines Proxys) */
      }
      if (res.ok && body.upload) return { ok: true, receivedBytes: body.upload.receivedBytes };
      return {
        ok: false,
        status: res.status,
        receivedBytes: body.receivedBytes,
        error: body.error ?? `HTTP ${res.status}`,
        code: body.code,
      };
    },

    async completeProjectMediaUpload(id: string, uploadId: string, preview: string | null) {
      return request<{ mediaId: string; project: ProjectDetail }>(
        `/projects/${encodeURIComponent(id)}/media/uploads/${encodeURIComponent(uploadId)}/complete`,
        { method: 'POST', body: JSON.stringify({ preview }) },
      );
    },

    async abortProjectMediaUpload(id: string, uploadId: string) {
      return request<{ success: boolean }>(
        `/projects/${encodeURIComponent(id)}/media/uploads/${encodeURIComponent(uploadId)}`,
        { method: 'DELETE' },
      );
    },

    async updateProjectMedia(
      id: string,
      mediaId: string,
      data: Partial<{ caption: string | null; phase: ProjectPhase; taskId: string | null; isReference: boolean }>,
    ) {
      return request<{ project: ProjectDetail }>(
        `/projects/${encodeURIComponent(id)}/media/${encodeURIComponent(mediaId)}`,
        { method: 'PATCH', body: JSON.stringify(data) },
      );
    },

    async deleteProjectMedia(id: string, mediaId: string) {
      return request<{ project: ProjectDetail }>(
        `/projects/${encodeURIComponent(id)}/media/${encodeURIComponent(mediaId)}`,
        { method: 'DELETE' },
      );
    },

    /** Original (Foto/Video) — berechtigungsgeprüft, mit Byte-Ranges; `download` = als Datei speichern. */
    getProjectMediaFileUrl(id: string, mediaId: string, download = false) {
      return `${API_BASE}/projects/${encodeURIComponent(id)}/media/${encodeURIComponent(mediaId)}/file${download ? '?download=1' : ''}`;
    },

    getProjectMediaPreviewUrl(id: string, mediaId: string) {
      return `${API_BASE}/projects/${encodeURIComponent(id)}/media/${encodeURIComponent(mediaId)}/preview`;
    },

    /** Alle Originale (oder nur die für Referenzen markierten) als ZIP. */
    getProjectMediaZipUrl(id: string, onlyReference = false) {
      return `${API_BASE}/projects/${encodeURIComponent(id)}/media/archive.zip${onlyReference ? '?reference=true' : ''}`;
    },

    // ── Vorlagen ───────────────────────────────────────────────────────────────
    async listProjectTemplates(includeArchived = false) {
      return request<{ templates: ProjectTemplate[] }>(
        `/project-templates${includeArchived ? '?includeArchived=true' : ''}`,
      );
    },

    async getProjectTemplate(id: string) {
      return request<{ template: ProjectTemplate }>(`/project-templates/${encodeURIComponent(id)}`);
    },

    async createProjectTemplate(data: ProjectTemplateInput) {
      return request<{ template: ProjectTemplate }>('/project-templates', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async updateProjectTemplate(id: string, data: ProjectTemplateInput) {
      return request<{ template: ProjectTemplate }>(`/project-templates/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    async setProjectTemplateArchived(id: string, archived: boolean) {
      return request<{ template: ProjectTemplate }>(
        `/project-templates/${encodeURIComponent(id)}/${archived ? 'archive' : 'unarchive'}`,
        { method: 'POST' },
      );
    },

    async duplicateProjectTemplate(id: string) {
      return request<{ template: ProjectTemplate }>(`/project-templates/${encodeURIComponent(id)}/duplicate`, {
        method: 'POST',
      });
    },

    async createProjectTemplateFromProject(data: { projectId: string; name: string; description?: string | null }) {
      return request<{ template: ProjectTemplate }>('/project-templates/from-project', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
  };
}
